import { ThunkDispatch } from "core-integration/src/store";
import { getElementControls } from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementControlsAndMaxCost,
} from "core-integration/src/store/elements/thunks";
import { IElement } from "core-integration/src/store/elements/types";
import { getAffordableElementsFromControls } from "core-integration/src/store/squad/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import { ellipsis } from "polished";
import { useEffect, useState } from "react";
import {
  useTranslation,
  WithTranslation,
  withTranslation,
} from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { getStatAbbrev, getStatDetails } from "../../utils/stats";
import Alert from "../Alert";
import Paginator, { paginate } from "../element-controls/Paginator";
import ElementDialogButton from "../ElementDialogButton";
import ElementShirt from "../ElementShirt";
import { ElementRow, ElementTable } from "../ElementTable";
import { Main, Wrapper } from "../Layout";
import Media from "../Media";
import Panel from "../Panel";
import ElementSearchField from "../squad/ElementSearchField";
import FilterChipRow from "../squad/SquadSelectionFilters/FilterChipRow";
import { ScrollTable } from "../Table";
import Title from "../Title";
import Tooltip, { TooltipLabel } from "../Tooltip";

const StatsStatus = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 7%;
  }
`;

const StatsElement = styled.th`
  width: 40%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 38%;
  }
`;

const StatsCost = styled.th`
  width: 15%;
  font-size: ${({ theme }) => theme.fontSizes[1]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsSel = styled.th`
  width: 15%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsForm = styled.th`
  width: 15%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsPts = styled.th`
  width: 18%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsExtra = styled.th`
  width: 15%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const ElementNameButton = styled.button`
  ${ellipsis()};
  width: 100%;
  padding: 0;
  border: 0;
  background-color: transparent;
  color: inherit;
  font-family: ${({ theme }) => theme.fonts.base};
  font-weight: bold;
  text-align: start;
  cursor: pointer;
`;

const Name = styled.div`
  ${ellipsis()};
  color: ${({ theme }) => theme.colors.white1};

  &:hover {
    text-decoration: underline;
  }
`;

const Team = styled.div`
  margin-inline-end: ${({ theme }) => theme.space[2]};
  font-size: ${({ theme }) => theme.fontSizes[1]};
`;

const SearchRow = styled.div`
  margin-bottom: ${({ theme }) => theme.space[2]};
`;

const Statistics = ({ t }: WithTranslation) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch>();
  const { statName } = useParams();
  const [page, setPage] = useState<number>(1);

  const controls = useSelector(getElementControls);
  const currencyDivisor = 10;
  const elements = useSelector(getAffordableElementsFromControls);
  const teamsById = useSelector(getTeamsById);

  const defaultStats: Array<keyof IElement> = [
    "now_cost",
    "selected_by_percent",
    "form",
    "total_points",
  ];

  useEffect(() => {
    dispatch(
      updateElementControlsAndMaxCost({
        ...controls,
        filter: "all",
        sort: statName || "total_points",
        search: "",
      }),
    );
  }, []);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [controls.filter, controls.sort, controls.maxCost, controls.search]);

  const { data, totalPages } = paginate(elements.data, page, 30);
  const extraStat =
    defaultStats.indexOf(controls.sort) === -1 ? controls.sort : "";
  const statDetail = getStatDetails(String(controls.sort), t);
  const extraStatLabel = extraStat
    ? (getStatDetails(String(extraStat), t)?.label ?? "")
    : "";
  const extraStatAbbrev = getStatAbbrev(extraStatLabel, i18n.language);

  return (
    <Wrapper>
      <Main>
        <Box mx={2} mb={4}>
          <Title>{t("statistics.title", "Statistics")}</Title>
        </Box>
        <SearchRow>
          <ElementSearchField />
        </SearchRow>
        <FilterChipRow initialSort={statName || "total_points"} />
        <Box mb="4" mt={4}>
          <Alert type="info" textAlign="start">
            {statDetail ? statDetail.description : ""}
          </Alert>
        </Box>
        <Panel>
          <ScrollTable>
            <ElementTable>
              <thead>
                <tr>
                  <StatsStatus>&nbsp;</StatsStatus>
                  <StatsElement>
                    {t("statistics.player", "Player")}
                  </StatsElement>
                  <StatsCost>{t("statistics.currency", "M SAR")}</StatsCost>
                  <StatsSel>
                    <Tooltip
                      content={t("statistics.selectedByLong", "Selected by %")}
                    >
                      <TooltipLabel>
                        {t("statistics.selectedByShort", "Sel.")}
                      </TooltipLabel>
                    </Tooltip>
                  </StatsSel>
                  <StatsForm>{t("statistics.form", "Form")}</StatsForm>
                  <StatsPts>
                    <Tooltip
                      content={t("statistics.totalPoints", "Total points")}
                    >
                      <TooltipLabel>
                        {t("statistics.totalPointsShort", "TP.")}
                      </TooltipLabel>
                    </Tooltip>
                  </StatsPts>
                  {extraStat ? (
                    <StatsExtra>
                      <Tooltip content={extraStatLabel}>
                        <TooltipLabel>{extraStatAbbrev}</TooltipLabel>
                      </Tooltip>
                    </StatsExtra>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {data.map((e) => (
                  <ElementRow key={e.id}>
                    <td>
                      <ElementDialogButton elementId={e.id} variant="list" />
                    </td>
                    <td>
                      <ElementNameButton
                        onClick={() => dispatch(showElementSummary(e.id))}
                      >
                        <Media
                          img={<ElementShirt elementId={e.id} sizes="24px" />}
                          centred={true}
                        >
                          <Name>{e.web_name}</Name>
                          <Team>{teamsById[e.team].short_name}</Team>
                        </Media>
                      </ElementNameButton>
                    </td>
                    <td>{integerToMoney(e.now_cost, currencyDivisor)}</td>
                    <td>{e.selected_by_percent}%</td>
                    <td>{e.form}</td>
                    <td>{e.total_points}</td>
                    {extraStat ? <td>{e[extraStat]}</td> : null}
                  </ElementRow>
                ))}
              </tbody>
            </ElementTable>
          </ScrollTable>
        </Panel>
        <Box mt={4} mb={6}>
          <Paginator totalPages={totalPages} page={page} setPage={setPage} />
        </Box>
      </Main>
    </Wrapper>
  );
};

export default withTranslation()(Statistics);
